import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, BadgeCheck, Check } from "lucide-react";
import {
  answerQuestion,
  createSummary,
  getCurrentQuestion,
  getVisibleQuestions,
  goToPreviousQuestion,
  questionBudgetForPace,
  startConversation,
  type ConversationState,
} from "@samklang/conversation-engine";
import { getConversationTemplate } from "@samklang/content";
import type { Perspective } from "@samklang/schemas";
import { Button, Card, Input, PageLoader } from "@samklang/ui";
import { defaultPreferences } from "@/stores/conversation-store";
import { useKartlaggningStore } from "@/stores/kartlaggning-store";
import { questionPresentation } from "@/domain/question-presentation";
import { AnswerPanel } from "@/features/conversation/AnswerPanel";
import { perspectiveLabel, perspectiveShortLabel, sourceLine } from "./lib";

interface RunProps {
  readonly kartlaggningId: string;
  readonly perspective: Perspective;
}

export function KartlaggningRunScreen({ kartlaggningId, perspective }: RunProps) {
  const navigate = useNavigate();
  const kartlaggning = useKartlaggningStore((store) => store.getKartlaggning(kartlaggningId));
  const saveRunProgress = useKartlaggningStore((store) => store.saveRunProgress);
  const completeRun = useKartlaggningStore((store) => store.completeRun);
  const setRunProvenance = useKartlaggningStore((store) => store.setRunProvenance);

  const template = kartlaggning ? getConversationTemplate(kartlaggning.templateId) : undefined;
  const run = kartlaggning?.runs.find((candidate) => candidate.perspective === perspective);

  const [state, setState] = useState<ConversationState>(() =>
    template ? startConversation(template, { maxQuestions: questionBudgetForPace("standard") }) : { status: "idle", responses: {}, orphanedResponseIds: [], maxQuestions: undefined, triggeredSafetyRuleIds: [], excludedQuestionKinds: [] },
  );

  const [conductedWith, setConductedWith] = useState(run?.conductedWith ?? "");
  const [conductedBy, setConductedBy] = useState(run?.conductedBy ?? "");
  const [registeredBy, setRegisteredBy] = useState(run?.registeredBy ?? "");

  if (!kartlaggning || !template || !run) {
    return (
      <section className="mx-auto max-w-2xl px-5 py-10">
        <Card>
          <h1 className="font-display text-2xl font-semibold">Perspektivet finns inte.</h1>
          <Button variant="ghost" className="mt-4" onClick={() => void navigate({ to: "/kartlaggningar" })}>Till listan</Button>
        </Card>
      </section>
    );
  }

  const answer = (response: Parameters<typeof answerQuestion>[2]) => {
    const next = answerQuestion(template, state, response);
    setState(next);
    saveRunProgress(kartlaggningId, perspective, next.responses);
  };

  const finish = () => {
    if (!registeredBy.trim()) return;
    const role = perspective === "annan" ? run.role : undefined;
    setRunProvenance(kartlaggningId, perspective, {
      conductedBy: conductedBy.trim(),
      conductedWith: conductedWith.trim(),
      registeredBy: registeredBy.trim(),
      ...(role ? { role } : {}),
    });
    const summary = createSummary(template, state);
    completeRun(kartlaggningId, perspective, state.responses, summary);
    void navigate({ to: "/kartlaggningar/$kartlaggningId", params: { kartlaggningId }, replace: true });
  };

  const viewAnswers = () => (
    <section className="mx-auto w-full max-w-3xl px-5 py-8 sm:px-8">
      <p className="text-sm font-semibold uppercase tracking-wider text-primary">{perspectiveLabel(perspective, run.role)}</p>
      <h1 className="font-display text-3xl font-semibold">{kartlaggning.title}</h1>
      <div className="mt-6">
        <Card>
          {run.summary && run.summary.length > 0 ? (
            <ul className="space-y-4">
              {run.summary.map((item) => (
                <li key={item.questionId} className="border-b border-border/50 pb-4 last:border-0">
                  <p className="font-semibold">{item.question}</p>
                  <p className="mt-1">{item.answer}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-foreground-muted">Inga registrerade svar.</p>
          )}
          <div className="mt-5 border-t border-border/50 pt-4 text-sm text-foreground-muted">{sourceLine(run, perspective)}</div>
          <Button variant="ghost" className="mt-4" onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId", params: { kartlaggningId } })}>
            <ArrowLeft size={18} /> Tillbaka till kartläggningen
          </Button>
        </Card>
      </div>
    </section>
  );

  if (run.status === "done") return viewAnswers();

  if (state.status === "completed") {
    const count = Object.keys(state.responses).length;
    return (
      <section className="mx-auto w-full max-w-2xl px-5 py-10 sm:px-8">
        <div className="text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-soft text-primary"><BadgeCheck size={34} /></span>
          <h1 className="mt-4 font-display text-3xl font-semibold">Klar — sammanställ nu</h1>
          <p className="mt-3 text-foreground-muted">
            {perspectiveLabel(perspective, run.role)} besvarade {count} frågor. Registrera närvaro och sammanställ underlaget.
          </p>
        </div>
        <Card className="mt-7">
          <h2 className="font-display text-lg font-semibold">Registrera närvaro</h2>
          <div className="mt-4 grid gap-4">
            <label className="grid gap-1 font-semibold">
              Genomfört av
              <Input value={conductedBy} onChange={(event) => setConductedBy(event.target.value)} maxLength={80} placeholder="t.ex. Specialpedagog M. Lind" />
            </label>
            <label className="grid gap-1 font-semibold">
              Genomfört tillsammans med
              <Input value={conductedWith} onChange={(event) => setConductedWith(event.target.value)} maxLength={80} placeholder="t.ex. Kurator A. Berg" />
            </label>
            <label className="grid gap-1 font-semibold">
              Registrerad av (krävs)
              <Input value={registeredBy} onChange={(event) => setRegisteredBy(event.target.value)} maxLength={80} placeholder="Vem registrerar underlaget?" />
            </label>
          </div>
          <Button className="mt-6" fullWidth disabled={!registeredBy.trim()} onClick={finish}>
            <Check size={20} /> Registrera och sammanställ
          </Button>
        </Card>
      </section>
    );
  }

  const question = getCurrentQuestion(template, state);
  if (!question) return <PageLoader label="Öppnar frågan" />;
  const visible = getVisibleQuestions(template, state.responses, state.maxQuestions, state.excludedQuestionKinds);
  const currentIndex = visible.findIndex((item) => item.id === question.id);
  const presentation = questionPresentation(question, defaultPreferences, perspective);

  return (
    <section className="mx-auto w-full max-w-3xl px-5 py-8 sm:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <button onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId", params: { kartlaggningId } })} className="text-sm font-semibold text-primary underline">
          Avbryt
        </button>
        <span className="text-sm font-medium text-foreground-muted">{perspectiveShortLabel(perspective, run.role)} · {currentIndex + 1} av {visible.length}</span>
      </div>

      <div className="mb-8">
        <p className="text-sm font-semibold uppercase tracking-wider text-primary">{perspectiveLabel(perspective, run.role)}</p>
        <h1 className="mt-2 font-display text-3xl font-semibold leading-tight sm:text-4xl">{presentation.title}</h1>
        {presentation.supportText ? <p className="mt-3 text-lg text-foreground-muted">{presentation.supportText}</p> : null}
      </div>

      <AnswerPanel question={question} visualSupport={presentation.visualSupport} fewerOptions={presentation.fewerOptions}
        onAnswer={(response) => answer(response as Parameters<typeof answerQuestion>[2])} />

      {currentIndex > 0 ? (
        <Button variant="ghost" className="mt-4" onClick={() => setState((current) => goToPreviousQuestion(template, current))}>
          <ArrowLeft size={18} /> Föregående fråga
        </Button>
      ) : null}
    </section>
  );
}
